import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState, } from "react";
import { usePopper } from "react-popper";
import useAuthViewer from "hooks/useAuthViewer";
import useReactions from "../../hooks/useReactions";
import ReactionIconStack, { REACTION_ICONS } from "../ReactionIconStack/ReactionIconStack";
import ReactionsModal from "../ReactionsModal/ReactionsModal";
import "./_style.scss";
const REACTIONS = REACTION_ICONS.map((r) => ({ ...r, label: r.id }));
// Stable reference — avoids reinitialising popper on every render.
const POPPER_MODIFIERS = [
    // Keep the popover anchored above the button, but slide it vertically
    // (altAxis) into view if it would overflow the viewport. tether: false lets
    // it detach from the button far enough to stay fully on-screen.
    { name: "preventOverflow", options: { altAxis: true, tether: false, padding: 8 } },
    { name: "offset", options: { offset: [0, 8] } },
    { name: "eventListeners", options: { scroll: false, resize: true } },
];
function ReactionsButton({ update, onReactionsChange }) {
    const { viewer } = useAuthViewer();
    const { addReaction, removeReaction } = useReactions(update.id);
    const [open, setOpen] = useState(false);
    const [pendingReaction, setPendingReaction] = useState(null);
    const [authenticatedViaModal, setAuthenticatedViaModal] = useState(false);
    const isLoggedIn = viewer !== null || authenticatedViaModal;
    const [counts, setCounts] = useState(() => {
        const c = {
            Solidarity: 0,
            Rallying: 0,
            Gratitude: 0,
            Victory: 0,
        };
        update.reactions.forEach(({ type, count }) => {
            c[type] = count;
        });
        return c;
    });
    const [myReaction, setMyReaction] = useState(update.authUserReaction);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const firstTileRef = useRef(null);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popupElement, setPopupElement] = useState(null);
    const popperOptions = useMemo(() => ({
        strategy: "fixed",
        placement: "top-start",
        modifiers: POPPER_MODIFIERS,
    }), []);
    const { styles: popperStyles, attributes: popperAttributes, update: updatePopper, } = usePopper(referenceElement, popupElement, popperOptions);
    useEffect(() => {
        if (open && updatePopper) {
            updatePopper();
        }
    }, [open, updatePopper]);
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handlePointer = (e) => {
            var _a;
            if (!((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                setOpen(false);
            }
        };
        const handleKey = (e) => {
            var _a;
            if (e.key === "Escape") {
                setOpen(false);
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        document.addEventListener("mousedown", handlePointer);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handlePointer);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const t = setTimeout(() => { var _a; return (_a = firstTileRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
        return () => clearTimeout(t);
    }, [open]);
    const totalCount = REACTIONS.reduce((sum, r) => sum + (counts[r.id] || 0), 0);
    const countsAsReactions = REACTION_ICONS.map((r) => ({
        type: r.id,
        count: counts[r.id] || 0,
    }));
    const handleTriggerClick = () => {
        setOpen((o) => !o);
    };
    const handleGuestTile = (reactionId) => {
        setOpen(false);
        setPendingReaction(reactionId);
    };
    const handleReact = (reactionId) => {
        var _a;
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        const next = { ...counts };
        let nextMine = myReaction;
        if (myReaction === reactionId) {
            next[reactionId] = Math.max(0, (next[reactionId] || 0) - 1);
            nextMine = null;
            removeReaction();
        }
        else {
            if (myReaction) {
                next[myReaction] = Math.max(0, (next[myReaction] || 0) - 1);
            }
            next[reactionId] = (next[reactionId] || 0) + 1;
            nextMine = reactionId;
            addReaction(reactionId);
        }
        setCounts(next);
        setMyReaction(nextMine);
        onReactionsChange === null || onReactionsChange === void 0 ? void 0 : onReactionsChange(REACTION_ICONS.map((r) => ({ type: r.id, count: next[r.id] || 0 })));
        setOpen(false);
    };
    const handleReacted = (reactionId, authenticated) => {
        const next = { ...counts };
        next[reactionId] = (next[reactionId] || 0) + 1;
        setCounts(next);
        setMyReaction(reactionId);
        if (authenticated) {
            setAuthenticatedViaModal(true);
        }
        setPendingReaction(null);
    };
    // Combine refs: triggerRef for focus management, setReferenceElement for popper.
    const setTriggerRef = (el) => {
        triggerRef.current = el;
        setReferenceElement(el);
    };
    return (_jsxs("div", { ref: wrapperRef, className: `reactions-button${myReaction ? ` --${myReaction.toLowerCase()}` : ""}`, children: [_jsxs("button", { ref: setTriggerRef, type: "button", className: "reactions-button__trigger", onClick: handleTriggerClick, "aria-haspopup": "dialog", "aria-expanded": open, children: [_jsx("span", { className: "reactions-button__icons", children: _jsx(ReactionIconStack, { reactions: countsAsReactions }) }), _jsx("span", { className: "reactions-button__label", children: totalCount > 0 ? `${totalCount.toLocaleString()} ${totalCount === 1 ? "Reaction" : "Reactions"}` : "React" })] }), pendingReaction !== null && (_jsx(ReactionsModal, { isOpen: true, onClose: () => setPendingReaction(null), updateId: update.id, selectedReaction: pendingReaction, onReacted: handleReacted })), _jsxs("div", { ref: setPopupElement, role: "dialog", className: `reactions-button__popup${!open ? " --hidden" : ""}`, style: popperStyles.popper, ...popperAttributes.popper, "aria-hidden": !open, children: [_jsx("div", { className: "reactions-button__popup-inner", children: REACTIONS.map((r, i) => {
                            const isMine = myReaction === r.id;
                            const idClass = r.id.toLowerCase();
                            return (_jsxs("button", { ref: i === 0 ? firstTileRef : undefined, type: "button", tabIndex: open ? 0 : -1, className: `reactions-button__tile --${idClass}${isMine ? " --reacted" : ""}`, onClick: () => (isLoggedIn ? handleReact(r.id) : handleGuestTile(r.id)), children: [_jsxs("div", { className: "reactions-button__tile-content", children: [_jsx("span", { className: `reactions-button__tile-icon --${idClass}${isMine ? " --reacted" : ""}`, children: r.icon }), _jsx("span", { className: `reactions-button__tile-label${isMine ? ` --reacted --${idClass}` : ""}`, children: r.label })] }), _jsx("div", { className: "reactions-button__tile-counter", children: counts[r.id] })] }, r.id));
                        }) }), _jsx("span", { className: "reactions-button__caret", "aria-hidden": "true" })] })] }));
}
export default ReactionsButton;
